import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const EXCHANGES = ['NYSE', 'NASDAQ', 'LSE', 'SGX', 'HKEX', 'ASX', 'XETRA', 'EURONEXT', 'TSX', 'AMEX'];
const CURRENCIES = ['USD', 'SGD', 'GBP', 'EUR', 'HKD', 'AUD', 'CAD'];
const US_EXCHANGES = new Set(['NYSE', 'NASDAQ', 'AMEX', 'US', '']);

const fmt = (n, digits = 0) =>
  n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const sign = n => n >= 0 ? '+' : '';

function MetricCard({ label, value, sub, subLabel, accentColor }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        borderTop: `3px solid ${accentColor}`,
        height: '100%',
      }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.03em', mt: 0.5, mb: 1.5 }}>
        {value}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
        {subLabel}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
        {sub}
      </Typography>
    </Paper>
  );
}

function GainCard({ gainPct, gainUSD, gainSGD }) {
  const positive = gainPct >= 0;
  const color = positive ? 'success.main' : 'error.main';
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        borderTop: `3px solid`,
        borderTopColor: color,
        height: '100%',
      }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
        Total Return
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, mb: 1.5 }}>
        {positive ? (
          <TrendingUpIcon sx={{ color: 'success.main', fontSize: 28 }} />
        ) : (
          <TrendingDownIcon sx={{ color: 'error.main', fontSize: 28 }} />
        )}
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.03em', color }}>
          {sign(gainPct)}{gainPct.toFixed(2)}%
        </Typography>
      </Stack>
      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={0.5}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {sign(gainUSD)}${fmt(gainUSD)} USD
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {sign(gainSGD)}${fmt(gainSGD)} SGD
        </Typography>
      </Stack>
    </Paper>
  );
}

function HoldingsTable({ rows, onRemove, onManualPrice }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ticker</TableCell>
            <TableCell>Exchange</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell align="right">Price</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Avg Cost</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Cost Basis</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Curr</TableCell>
            <TableCell align="right">Value</TableCell>
            <TableCell align="right">Return</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>% Port</TableCell>
            <TableCell align="right" padding="none" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(h => {
            const change = h.lifetimeChange || 0;
            const positive = change >= 0;
            return (
              <TableRow key={h.ticker} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {h.ticker}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={h.exchange || '—'} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }} />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {(h.latestQuantity || 0).toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {h.latestPrice > 0 ? h.latestPrice.toFixed(2) : (
                    <IconButton size="small" onClick={() => onManualPrice(h.ticker)} title="Set manual price" sx={{ p: 0.25 }}>
                      <EditIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', display: { xs: 'none', md: 'table-cell' } }}>
                  {(h.weightedAvgCost || 0).toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', display: { xs: 'none', sm: 'table-cell' } }}>
                  {(h.remainingCost || 0).toFixed(2)}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {h.currency || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {h.paperValue > 0 ? fmt(h.paperValue, 2) : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: positive ? 'success.main' : 'error.main', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {sign(change)}{(change * 100).toFixed(2)}%
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {((h.percentOfHoldings || 0) * 100).toFixed(1)}%
                  </Typography>
                </TableCell>
                <TableCell align="right" padding="none">
                  <IconButton size="small" onClick={() => onRemove(h.ticker)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function InvestmentHoldingsPage() {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [exchangeRate, setExchangeRate] = useState(1.35);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ ticker: '', exchange: 'NASDAQ', currency: 'USD' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  const [showManualModal, setShowManualModal] = useState(false);
  const [manualSymbol, setManualSymbol] = useState('');
  const [manualPrice, setManualPrice] = useState('');
  const [manualCurrency, setManualCurrency] = useState('USD');

  useEffect(() => {
    axios.get('https://api.exchangerate-api.com/v4/latest/USD', { withCredentials: false })
      .then(res => { if (res.data?.rates?.SGD) setExchangeRate(res.data.rates.SGD); })
      .catch(() => {});
  }, []);

  const fetchHoldings = async () => {
    try {
      setLoading(true); setError('');
      const res = await api.get('/api/investments/holdings');
      setHoldings(res.data.holdings || []);
      if (res.data.lastUpdated) setLastRefreshed(res.data.lastUpdated);
    } catch (err) {
      setError('Error loading holdings: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchHoldings(); }, []);

  const metrics = (() => {
    const R = 1 / exchangeRate;
    let vUSD = 0, cUSD = 0;
    holdings.forEach(h => {
      if (h.currency === 'USD') { vUSD += h.paperValue; cUSD += h.remainingCost; }
      else if (h.currency === 'SGD') { vUSD += h.paperValue * R; cUSD += h.remainingCost * R; }
    });
    const vSGD = vUSD * exchangeRate, cSGD = cUSD * exchangeRate;
    const gUSD = vUSD - cUSD, gSGD = vSGD - cSGD;
    const gPct = cUSD > 0 ? (gUSD / cUSD) * 100 : 0;
    return { vUSD, cUSD, vSGD, cSGD, gUSD, gSGD, gPct };
  })();

  const handleRefresh = async () => {
    try {
      setRefreshing(true); setRefreshMessage(''); setError('');
      const res = await api.post('/api/investments/refresh-prices');
      if (res.data.success) {
        setHoldings(res.data.holdings);
        setLastRefreshed(res.data.lastUpdated);
        setRefreshMessage('Prices refreshed');
      } else setError('Failed: ' + res.data.error);
    } catch (err) {
      setError('Error: ' + (err.response?.data?.error || err.message));
    } finally { setRefreshing(false); }
  };

  const handleAddTicker = async () => {
    if (!addForm.ticker.trim()) { setAddError('Ticker is required'); return; }
    try {
      setAddLoading(true); setAddError('');
      await api.post('/api/investments/tickers', {
        ticker: addForm.ticker.trim().toUpperCase(),
        exchange: addForm.exchange,
        currency: addForm.currency,
      });
      setShowAddModal(false);
      setAddForm({ ticker: '', exchange: 'NASDAQ', currency: 'USD' });
      await fetchHoldings();
      setRefreshMessage(`${addForm.ticker.toUpperCase()} added`);
    } catch (err) { setAddError(err.response?.data?.error || err.message); }
    finally { setAddLoading(false); }
  };

  const handleRemove = async (ticker) => {
    if (!window.confirm(`Remove ${ticker}?`)) return;
    try {
      setError('');
      await api.delete(`/api/investments/tickers/${ticker}`);
      await fetchHoldings();
    } catch (err) { setError('Error: ' + (err.response?.data?.error || err.message)); }
  };

  const openManualPrice = (symbol = '') => { setManualSymbol(symbol); setShowManualModal(true); };

  const handleSetManualPrice = async () => {
    if (!manualSymbol || !manualPrice) { setError('Symbol and price required'); return; }
    try {
      await api.post('/api/investments/manual-prices', {
        symbol: manualSymbol, price: parseFloat(manualPrice), currency: manualCurrency,
      });
      setRefreshMessage(`Price set for ${manualSymbol}`);
      setShowManualModal(false);
      setManualSymbol(''); setManualPrice(''); setManualCurrency('USD');
      await fetchHoldings();
    } catch (err) { setError('Error: ' + (err.response?.data?.error || err.message)); }
  };

  const usHoldings = holdings.filter(h => US_EXCHANGES.has((h.exchange || '').toUpperCase()));
  const intlHoldings = holdings.filter(h => !US_EXCHANGES.has((h.exchange || '').toUpperCase()));

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={2.5}>
        <Box>
          <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em">Portfolio Holdings</Typography>
          <Typography variant="body2" color="text.secondary">Track your investments across markets</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => { setShowAddModal(true); setAddError(''); }}>
            Add Ticker
          </Button>
          <Button variant="outlined" size="small" startIcon={refreshing ? <CircularProgress size={12} /> : <RefreshIcon />}
            onClick={handleRefresh} disabled={refreshing || loading}>
            {refreshing ? 'Refreshing…' : 'Refresh Prices'}
          </Button>
          <Button variant="outlined" size="small" startIcon={<EditIcon />}
            onClick={() => openManualPrice('')}>
            Manual Price
          </Button>
        </Stack>
      </Stack>

      {/* Status */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" mb={2}>
        {lastRefreshed && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
            Updated {new Date(lastRefreshed).toLocaleString()}
          </Typography>
        )}
        {refreshMessage && <Alert severity="success" sx={{ py: 0, px: 1.5 }}>{refreshMessage}</Alert>}
        {error && <Alert severity="error" sx={{ py: 0, px: 1.5 }}>{error}</Alert>}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : holdings.length === 0 ? (
        <Paper variant="outlined" sx={{ textAlign: 'center', py: 8, borderRadius: 3 }}>
          <Typography color="text.secondary" mb={2}>No tickers tracked yet.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddModal(true)}>
            Add Ticker
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {/* Metric cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <MetricCard
              label="Portfolio Value · USD"
              value={`$${fmt(metrics.vUSD)}`}
              subLabel="Cost Basis"
              sub={`$${fmt(metrics.cUSD)}`}
              accentColor="#f97316"
            />
            <MetricCard
              label="Portfolio Value · SGD"
              value={`$${fmt(metrics.vSGD)}`}
              subLabel="Cost Basis"
              sub={`$${fmt(metrics.cSGD)}`}
              accentColor="#8b5cf6"
            />
            <GainCard gainPct={metrics.gPct} gainUSD={metrics.gUSD} gainSGD={metrics.gSGD} />
          </Box>

          {/* US Markets */}
          {usHoldings.length > 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="overline" sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.primary' }}>
                  US Markets
                </Typography>
                <Chip label="NYSE · NASDAQ · AMEX" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
              </Box>
              <HoldingsTable rows={usHoldings} onRemove={handleRemove} onManualPrice={openManualPrice} />
            </Paper>
          )}

          {/* International */}
          {intlHoldings.length > 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="overline" sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.primary' }}>
                  International
                </Typography>
                <Chip label="LSE · SGX · HKEX · ASX" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>Use manual price</Typography>
              </Box>
              <HoldingsTable rows={intlHoldings} onRemove={handleRemove} onManualPrice={openManualPrice} />
            </Paper>
          )}
        </Stack>
      )}

      {/* Add Ticker Dialog */}
      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Ticker</DialogTitle>
        <DialogContent>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Ticker Symbol"
              value={addForm.ticker}
              onChange={e => setAddForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
              placeholder="e.g. AAPL, VWRA, D05"
              autoFocus
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>Exchange</InputLabel>
              <Select label="Exchange" value={addForm.exchange}
                onChange={e => setAddForm(f => ({ ...f, exchange: e.target.value }))}>
                {EXCHANGES.map(ex => <MenuItem key={ex} value={ex}>{ex}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Currency</InputLabel>
              <Select label="Currency" value={addForm.currency}
                onChange={e => setAddForm(f => ({ ...f, currency: e.target.value }))}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleAddTicker} variant="contained" size="small" disabled={addLoading}>
            {addLoading ? 'Adding…' : 'Add Ticker'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Price Dialog */}
      <Dialog open={showManualModal} onClose={() => setShowManualModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Manual Price</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Symbol"
              value={manualSymbol}
              onChange={e => setManualSymbol(e.target.value.toUpperCase())}
              placeholder="e.g. IWDA, VWRA, D05"
              autoFocus
              fullWidth
            />
            <TextField
              label="Price"
              type="number"
              inputProps={{ step: '0.01' }}
              value={manualPrice}
              onChange={e => setManualPrice(e.target.value)}
              placeholder="e.g. 95.50"
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>Currency</InputLabel>
              <Select label="Currency" value={manualCurrency} onChange={e => setManualCurrency(e.target.value)}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManualModal(false)} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleSetManualPrice} variant="contained" size="small">Set Price</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
